/**
 * As listas (#922): o que a tela mostra num dropdown ou numa tabela, lido AO
 * VIVO do banco do cliente por uma operação de listagem do serviço de
 * automações.
 *
 * O CRM guarda só a definição; as linhas nunca são gravadas, e cada leitura
 * consulta o cliente. Genéricas para qualquer organização: a `finalidade` é um
 * texto livre (`exames`, `produtos`…), e a tela que consome pede pela
 * finalidade.
 *
 * Ler é de todos os papéis; montar, publicar e arquivar, só da equipe técnica
 * da Cliqfy (`operacao_cliqfy`, ADR-0021, revisão de 19/10).
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

export const FinalidadeDaListaSchema = z
  .string()
  .regex(/^[a-z0-9_]{2,40}$/, 'letras minúsculas, números e _, de 2 a 40 caracteres');

export const SituacaoDaListaSchema = z.enum(['rascunho', 'publicada', 'arquivada']);

export const ColunaDaListaSchema = z
  .object({
    /** O nome da coluna no resultado da operação. */
    nome: z.string(),
    /** O título na tela. */
    titulo: z.string(),
    tipo: z.enum(['texto', 'numero', 'booleano', 'data']),
  })
  .strict();

/**
 * Que parâmetro da operação recebe que identificador do cliente: `telefone`
 * (E.164), `documento` (só dígitos) ou `codigo_externo:<sistema>` (o id do
 * cliente naquele sistema, pelos vínculos de identidade).
 */
export const FiltroPorClienteSchema = z
  .object({
    parametro: z.string().regex(/^[a-z_][a-z0-9_]{0,63}$/),
    fonte: z
      .string()
      .regex(
        /^(telefone|documento|codigo_externo:[a-z0-9_.-]{1,60})$/,
        'use telefone, documento ou codigo_externo:<sistema>',
      ),
  })
  .strict();

export const ListaSchema = z
  .object({
    id: IdSchema,
    nome: z.string(),
    descricao: z.string().nullable(),
    finalidade: z.string(),
    /** A operação de listagem no serviço (`/v1/integracoes-de-dados/operacoes/{id}`). */
    operacao_id: IdSchema,
    /** O nome da conexão quando a lista foi salva: só para exibir. */
    conexao_nome: z.string().nullable(),
    coluna_chave: z.string(),
    coluna_rotulo: z.string(),
    colunas: z.array(ColunaDaListaSchema),
    filtro_por_cliente: FiltroPorClienteSchema.nullable(),
    situacao: SituacaoDaListaSchema,
    /** Devolvida no PATCH: gravar sobre uma versão velha é 409. */
    versao: z.number().int(),
    criada_em: z.string(),
    atualizada_em: z.string(),
    publicada_em: z.string().nullable(),
  })
  .strict();

export type ListaDaApi = z.infer<typeof ListaSchema>;

export const ListasSchema = z.object({ itens: z.array(ListaSchema) }).strict();

const CamposDaLista = {
  nome: z.string().trim().min(1).max(120),
  descricao: z.string().trim().max(500).nullable().default(null),
  finalidade: FinalidadeDaListaSchema,
  operacao_id: IdSchema,
  coluna_chave: z.string().min(1).max(128),
  coluna_rotulo: z.string().min(1).max(128),
  /** As colunas mostradas, na ordem. O tipo vem da operação. */
  colunas: z
    .array(
      z
        .object({ nome: z.string().min(1).max(128), titulo: z.string().trim().min(1).max(120) })
        .strict(),
    )
    .min(1)
    .max(50),
  filtro_por_cliente: FiltroPorClienteSchema.nullable().default(null),
};

export const CriarListaSchema = z.object(CamposDaLista).strict();

export type CriarLista = z.infer<typeof CriarListaSchema>;

/** Editar troca a definição inteira; `versao` é a que a tela leu. */
export const EditarListaSchema = z
  .object({ ...CamposDaLista, versao: z.number().int().min(1) })
  .strict();

export type EditarLista = z.infer<typeof EditarListaSchema>;

/** A prévia de quem monta: lê também o rascunho. */
export const LerListaSchema = z.object({ cliente_id: IdSchema.optional() }).strict();

const CamposDaLeitura = {
  /** O total antes do corte do limite da operação. */
  total: z.number().int(),
  cortada: z.boolean(),
  /** A lista filtra pelo cliente, e o cliente não tem o identificador: nada foi lido. */
  cliente_sem_identificador: z.boolean(),
  /** Quando o banco do cliente respondeu. Nada disto é gravado no CRM. */
  lida_em: z.string(),
};

export const LeituraDaListaSchema = z
  .object({
    colunas: z.array(ColunaDaListaSchema),
    linhas: z.array(z.record(z.string(), z.unknown())),
    ...CamposDaLeitura,
  })
  .strict();

export const ItemDaListaSchema = z
  .object({
    /** O valor da coluna-chave, em texto: é o que a tela guarda ao escolher. */
    valor: z.string(),
    rotulo: z.string(),
    /** As colunas declaradas da linha, para a tela mostrar ao lado. */
    dados: z.record(z.string(), z.unknown()),
  })
  .strict();

export const ItensDaListaSchema = z
  .object({ itens: z.array(ItemDaListaSchema), ...CamposDaLeitura })
  .strict();

// ── Pedidos de lista ──────────────────────────────────────────────────────────

export const SituacaoDoPedidoSchema = z.enum(['aguardando', 'atendido', 'recusado']);

/** O pedido de uma lista nova: a organização descreve, a Cliqfy atende ou recusa. */
export const PedidoDeListaSchema = z
  .object({
    id: IdSchema,
    nome: z.string(),
    o_que: z.string(),
    colunas_sugeridas: z.string().nullable(),
    situacao: SituacaoDoPedidoSchema,
    pedido_por: IdSchema,
    decidido_por: IdSchema.nullable(),
    /** Atendido: a lista montada (`/v1/listas/{id}`). */
    lista_id: IdSchema.nullable(),
    /** Recusado: por quê. */
    motivo: z.string().nullable(),
    criado_em: z.string(),
    decidido_em: z.string().nullable(),
  })
  .strict();

export type PedidoDeListaDaApi = z.infer<typeof PedidoDeListaSchema>;

export const PedidosDeListaSchema = z.object({ itens: z.array(PedidoDeListaSchema) }).strict();

export const PedirListaSchema = z
  .object({
    nome: z.string().trim().min(1).max(120),
    /** O que a lista deve mostrar e para quê. */
    o_que: z.string().trim().min(1).max(2000),
    colunas_sugeridas: z.string().trim().max(1000).nullable().default(null),
  })
  .strict();

export type PedirLista = z.infer<typeof PedirListaSchema>;

export const AtenderPedidoDeListaSchema = z.object({ lista_id: IdSchema }).strict();

export const RecusarPedidoDeListaSchema = z
  .object({ motivo: z.string().trim().min(1).max(500) })
  .strict();
